import React, { useState } from 'react';

/**
 * CDG-SQL Intelligence Panel — CID-DDLS-GenAI SQL
 * 
 * Renders below the query area when CDG-SQL flow is active.
 * Shows: Step 1 status → L1 choice → L2 confirm → L3 pattern badge
 * 
 * DOES NOT modify IntelligenceChart.js, ZDDV, Executive Dashboard,
 * or ClientApplication.js visualization code.
 * 
 * Acronym Reference:
 *   CDG-SQL = CEO-DDLS-GenAI SQL (CID-DDLS-GenAI SQL)
 *   CID = CEO Intelligence Directives
 *   DDLS = Deep Dive Learning Session
 *   BAI = Business Atomic Intelligence
 *   COA-GPT = Course of Action GPT
 *   PNP-BAI = Plug-N-Play Business Atomic Intelligence
 *   L1 = Level 1 (User Choice)
 *   L2 = Level 2 (Confirm)
 *   L3 = Level 3 (Permanent Pattern)
 */
export default function CdgSqlPanel({ cdgState, clientId, productType, dbConfig, onSqlConfirmed, onRetry }) {
  const [userSql, setUserSql] = useState('');
  const [editSql, setEditSql] = useState('');
  const [loading, setLoading] = useState(false);
  const [localState, setLocalState] = useState(null);

  // Use localState if we have it (from L1/L2 actions), otherwise use parent cdgState
  const state = localState || cdgState;

  if (!state) return null;

  const { status, next_step, sql_used, error, session_id, source, cdg_tooltip,
          available_tables, message, latency_ms, model, pattern_id, original_query } = state;

  // ─── L3: Permanent Pattern Badge ──────────────────────────────────────────
  if (source === 'permanent_pattern_L3' && status === 'success') {
    return (
      <div style={{ margin: '8px 0', padding: '8px 14px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '8px', fontSize: '11px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>🔒</span>
          <span style={{ fontWeight: 600, color: '#1b5e20' }}
            title="Level 3 Permanent Pattern — This SQL is locked and will be reused for identical queries until you modify it">
            Permanent Pattern (L3)
          </span>
          <button onClick={() => {
            if (sql_used) navigator.clipboard.writeText(sql_used);
          }} title="Copy full SQL to clipboard" style={{
            padding: '2px 8px', background: '#fff', border: '1px solid #a5d6a7',
            borderRadius: '4px', fontSize: '9px', cursor: 'pointer', color: '#1b5e20',
          }}>📋 Copy SQL</button>
          <button onClick={() => {
            if (sql_used && onRetry) {
              // Trigger re-execution of the permanent SQL
              onRetry(sql_used);
            }
          }} title="Re-execute this permanent SQL and show results with data visualizations" style={{
            padding: '2px 8px', background: '#2e7d32', border: 'none',
            borderRadius: '4px', fontSize: '9px', cursor: 'pointer', color: '#fff', fontWeight: 600,
          }}>▶ Run SQL</button>
          <span style={{ marginLeft: 'auto', color: '#888', fontSize: '10px' }}
            title="CDG-SQL = CEO-DDLS-GenAI SQL: SQL generated by combining CID (CEO Intelligence Directives), DDLS (Deep Dive Learning Session) Section 6 recommendations, and GenAI schema context. Powered by COA-GPT (Course of Action GPT)">
            CDG-SQL
          </span>
        </div>
        {sql_used && (
          <div style={{ marginTop: '6px', background: '#1a1a2e', borderRadius: '4px', padding: '6px 10px', maxHeight: '60px', overflowY: 'auto', cursor: 'text', userSelect: 'all' }}>
            <code style={{ fontSize: '9px', color: '#a5d6a7', fontFamily: 'Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {sql_used}
            </code>
          </div>
        )}
      </div>
    );
  }

  // ─── L2: Confirm Panel ────────────────────────────────────────────────────
  if (next_step === 'l2_confirm' && status === 'success') {
    return (
      <div style={{ margin: '8px 0', padding: '12px 14px', background: '#fff8e1', border: '1px solid #ffcc80', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px' }}>✅</span>
          <span style={{ fontWeight: 600, fontSize: '12px', color: '#e65100' }}
            title="CDG-SQL Level 2 Confirm — SQL executed successfully. Confirm to save as a Permanent Pattern (L3) for future reuse">
            SQL Executed Successfully — Confirm as Permanent?
          </span>
          <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#888' }}>{latency_ms}ms | {model || source}</span>
        </div>
        <div style={{ background: '#1a1a2e', borderRadius: '6px', padding: '8px 12px', marginBottom: '8px', maxHeight: '80px', overflowY: 'auto' }}>
          <code style={{ fontSize: '10px', color: '#a5d6a7', fontFamily: 'Consolas, monospace', whiteSpace: 'pre-wrap' }}>
            {editSql || sql_used}
          </code>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={async () => {
            setLoading(true);
            try {
              const sqlToConfirm = editSql || sql_used || '';
              const res = await fetch(`/v1/clients/${clientId}/cdg-sql/confirm`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: session_id || 'direct', confirmed_sql: sqlToConfirm }),
              });
              if (res.ok) {
                setLocalState({ ...state, source: 'permanent_pattern_L3', status: 'success', next_step: 'done' });
                if (onSqlConfirmed) onSqlConfirmed({ confirmed_sql: sqlToConfirm });
              }
            } catch (e) { console.error(e); }
            setLoading(false);
          }} disabled={loading}
            title="Confirm this SQL as a Level 3 Permanent Pattern — it will be reused automatically for identical Business Use Case queries"
            style={{
              padding: '5px 14px', background: '#2e7d32', color: '#fff', border: 'none',
              borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
            }}>
            {loading ? '...' : '✓ Confirm as Permanent'}
          </button>
          <button onClick={() => {
            const currentSql = editSql || sql_used || '';
            const newSql = prompt('Modify SQL:', currentSql);
            if (newSql && newSql.trim()) setEditSql(newSql.trim());
          }}
            title="Modify the SQL before confirming — your changes will be tested on execution"
            style={{
              padding: '5px 14px', background: '#fff', color: '#1565c0', border: '1px solid #1565c0',
              borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
            }}>
            ✏️ Modify
          </button>
          <span style={{ fontSize: '9px', color: '#888', marginLeft: 'auto' }}
            title="CDG-SQL = CEO-DDLS-GenAI SQL: CID (CEO Intelligence Directives) + DDLS (Deep Dive Learning Session) Section 6 + GenAI schema context. Model: COA-GPT (Course of Action GPT)">
            💡 CDG-SQL
          </span>
        </div>
      </div>
    );
  }

  // ─── L1: User Choice Panel ────────────────────────────────────────────────
  if (next_step === 'l1_choice') {
    return (
      <div style={{ margin: '8px 0', padding: '12px 14px', background: '#fce4ec', border: '1px solid #f48fb1', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
          title="CDG-SQL (CEO-DDLS-GenAI SQL) Level 2: The initial query could not produce results. Choose to provide your own SQL or let PNP-BAI (Business Atomic Intelligence) generate CDG-SQL using COA-GPT (Course of Action GPT)">
          <span style={{ fontSize: '14px' }}>⚠️</span>
          <span style={{ fontWeight: 600, fontSize: '12px', color: '#880e4f' }}>
            {sql_used ? 'SQL Error — Choose Your Path (CDG-SQL Level 2)' : 'COA-GPT Could Not Resolve — CDG-SQL Level 2'}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: '9px', color: '#888' }}
            title="PNP-BAI = Plug-N-Play Business Atomic Intelligence | CDG-SQL = CEO-DDLS-GenAI SQL | COA-GPT = Course of Action GPT">
            💡 CDG-SQL L2
          </span>
        </div>
        {error && (
          <div style={{ background: '#fff', borderRadius: '4px', padding: '6px 10px', marginBottom: '8px', fontSize: '10px', color: '#c62828', fontFamily: 'monospace', maxHeight: '40px', overflow: 'hidden' }}
            title="Error from COA-GPT (Course of Action GPT) SQL execution. Use CDG-SQL Level 2 options below to resolve.">
            {error}
          </div>
        )}
        {sql_used && (
          <div style={{ background: '#1a1a2e', borderRadius: '4px', padding: '6px 10px', marginBottom: '8px', maxHeight: '50px', overflowY: 'auto' }}
            title="The SQL that was attempted by COA-GPT (Course of Action GPT) but failed execution">
            <code style={{ fontSize: '9px', color: '#ef9a9a', fontFamily: 'Consolas, monospace', whiteSpace: 'pre-wrap' }}>
              Failed SQL: {sql_used}
            </code>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          {/* Option B: PNP-BAI generates CDG-SQL */}
          <button onClick={async () => {
            setLoading(true);
            try {
              const res = await fetch(`/v1/clients/${clientId}/cdg-sql/generate`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  session_id,
                  product_type: productType,
                  query: original_query || '',
                  failed_sql: sql_used || '',
                  error: error || '',
                  db_config: dbConfig || null,
                }),
              });
              if (!res.ok) {
                setLocalState({
                  status: 'error',
                  error: `Server returned ${res.status}: ${res.statusText}. The backend CDG-SQL service may not be running.`,
                  sql_used: sql_used || '',
                  source: 'network_error',
                  session_id,
                  next_step: 'l1_choice',
                  original_query,
                  latency_ms: 0,
                });
                setLoading(false);
                return;
              }
              const data = await res.json();
              // Preserve original_query for subsequent retries
              if (!data.original_query) data.original_query = original_query;
              setLocalState(data);
            } catch (e) {
              console.error('[CDG-SQL] Network error:', e);
              setLocalState({
                status: 'error',
                error: `Network error: Could not reach the backend server. Ensure the PNP-BAI server is running (http://localhost:8000 or production endpoint).`,
                sql_used: sql_used || '',
                source: 'network_error',
                session_id,
                next_step: 'l1_choice',
                original_query,
                latency_ms: 0,
              });
            }
            setLoading(false);
          }} disabled={loading}
            title="PNP-BAI (Business Atomic Intelligence) generates CDG-SQL (CEO-DDLS-GenAI SQL) using COA-GPT (Course of Action GPT) — combines CID (CEO Intelligence Directives), DDLS Section 6 recommendations, and schema context to produce corrected SQL"
            style={{
              padding: '6px 14px', background: '#1565c0', color: '#fff', border: 'none',
              borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', flex: 1,
            }}>
            {loading ? '⏳ Generating...' : '🤖 Let PNP-BAI Generate CDG-SQL'}
          </button>
        </div>
        {/* Option A: User provides SQL */}
        <div style={{ display: 'flex', gap: '6px' }}>
          <input value={userSql} onChange={e => setUserSql(e.target.value)}
            placeholder="Or paste your SQL here..."
            title="Provide your own SQL query — it will be executed directly against your database"
            style={{ flex: 1, padding: '5px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '11px', fontFamily: 'monospace' }}
          />
          <button onClick={async () => {
            if (!userSql.trim()) return;
            setLoading(true);
            try {
              const res = await fetch(`/v1/clients/${clientId}/cdg-sql/user-sql`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sql: userSql, session_id, product_type: productType, db_config: dbConfig || null }),
              });
              if (!res.ok) {
                setLocalState({
                  status: 'error',
                  error: `Server returned ${res.status}: ${res.statusText}. Backend may not be running.`,
                  sql_used: userSql,
                  source: 'network_error',
                  session_id,
                  next_step: 'l1_choice',
                  original_query,
                  latency_ms: 0,
                });
                setLoading(false);
                return;
              }
              const data = await res.json();
              setLocalState(data);
            } catch (e) {
              console.error('[CDG-SQL] User SQL network error:', e);
              setLocalState({
                status: 'error',
                error: `Network error: Could not reach the backend. Ensure the server is running.`,
                sql_used: userSql,
                source: 'network_error',
                session_id,
                next_step: 'l1_choice',
                original_query,
                latency_ms: 0,
              });
            }
            setLoading(false);
          }} disabled={loading || !userSql.trim()}
            title="Execute your SQL — on success, you can confirm it as a Permanent Pattern (L3)"
            style={{
              padding: '5px 12px', background: userSql.trim() ? '#4caf50' : '#ccc', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '11px', cursor: userSql.trim() ? 'pointer' : 'default',
            }}>
            ▶ Execute
          </button>
        </div>
        {available_tables && available_tables.length > 0 && (
          <div style={{ marginTop: '6px', fontSize: '9px', color: '#888' }}
            title="Available database tables that CDG-SQL can query against">
            Tables: {available_tables.slice(0, 10).join(', ')}{available_tables.length > 10 ? '...' : ''}
          </div>
        )}
      </div>
    );
  }

  // ─── Pattern Failed ───────────────────────────────────────────────────────
  if (status === 'pattern_failed') {
    return (
      <div style={{ margin: '8px 0', padding: '10px 14px', background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px', fontSize: '11px' }}
        title="A previously confirmed Permanent Pattern (L3) failed — the database schema may have changed. Re-enter CDG-SQL flow to generate new SQL.">
        <span style={{ fontWeight: 600, color: '#e65100' }}>⚠️ Saved pattern failed</span>
        <span style={{ color: '#666', marginLeft: '8px' }}>{message}</span>
      </div>
    );
  }

  return null;
}
